import { action, observable, computed, makeObservable } from 'mobx';

import { EntryFieldData, EntryTypeFieldName } from 'chaika-frontend-types/frontend-cms';

import { commonService } from '@store/common/commonService';
import { IField } from './Field';
import { toArrayFromIterable } from '@utils/toArrayFromIterable';

export class EntryFields {
    @observable public rawFields: EntryFieldData[];

    @observable private fields = new Map<string, IField>();
    @observable private setedValueFieldNames = new Set<string>();

    constructor(fields: EntryFieldData[] = [] as EntryFieldData[]) {
        makeObservable(this);

        this.fields.clear();

        this.rawFields = fields;
        fields.forEach(field => {
            const value = commonService.createFieldValue(field.name, field.value);
            this.setedValueFieldNames.add(field.name);
            this.fields.set(field.name, value);
        });
    }

    @computed
    public get fieldNames(): string[] {
        return Array.from(this.fields.keys());
    }

    public get fieldsPreviewWithName(): { name: string; value: string }[] {
        return toArrayFromIterable(this.fields).map(field => ({ name: field.name, value: field.getPreview() }));
    }

    public get fieldsPreviewWithNameObject(): Record<string, string> {
        return this.fieldsPreviewWithName.reduce<Record<string, string>>((acc, curr) => {
            acc[curr.name] = curr.value;
            return acc;
        }, {});
    }

    @action
    public reset(): void {
        this.fields.clear();
    }

    @action
    public removeField(name: string): void {
        this.fields.delete(name);
    }

    public isValueSet(name: string): boolean {
        return this.setedValueFieldNames.has(name);
    }

    public get(name: string): IField {
        let result = this.fields.get(name);

        if (!result) {
            const value = name === EntryTypeFieldName.Referrals ? [] : null;
            this.set(name, value, true);
            result = this.get(name);
        }

        return result;
    }

    public hasValue(name: string): boolean {
        return this.get(name).hasData;
    }

    public getValue<T>(name: string): T {
        const field = this.get(name);

        return field ? field.getValue() : ('' as T);
    }

    @action
    public set(name: string | EntryTypeFieldName, value: any, disableAddValueSet = false) {
        const filedValue = commonService.createFieldValue(name, value);
        if (!disableAddValueSet) {
            this.setedValueFieldNames.add(name);
        }
        this.fields.set(name, filedValue);
    }
}
